// The front door's hero: one real practice at a time, large, cross-fading to
// the next (30 Jul 2026). A grid was tried first and cut the same hour: the
// page already carries a lot of words, and twelve captions at once is a wall
// to read rather than a thing to look at.
//
// Every slide names the expert it came from. That is not a courtesy line: the
// whole proposition is that these are their practices, not ours, and an
// unattributed instruction on a landing page reads as Lab 10 telling people
// what to do. The evidence label rides along for the same reason, so the
// grade is visible before anyone signs up.
//
// Since 2 Aug 2026 (Rob) the deck also carries the disagreements, two
// practices to a question, rather than putting them in a second slideshow
// further down the page. One hero that alternates says both halves of the
// product at once: here is what these people tell you to do, and here is
// where they contradict each other. A second hero would have said the second
// half below the fold, to whoever scrolled.
//
// Why practice art and not the roster's faces: the portrait rights ledger
// marks most of that set "All rights reserved or not stated", and this is the
// most public surface in the product. These illustrations are ours.
//
// The clock, the deck, and the corner rotation live in ./slideshow, shared
// with Discover's hero (31 Jul 2026).
import { Fragment, useRef } from "react";
import heroart from "../heroart.json";
import { HERO_SIZES_SOLO, sentence, shuffled, useMajorityVisible, useSlideshow, type Slide } from "./slideshow.js";
import { artImg } from "../assets.js";

interface PracticeSlide extends Slide {
  kind: "practice";
  chip: string | null;
  expert: string;
  url: string | null;
}

interface Person {
  name: string;
  // Where this person put the position. Null on the six questions out of 170
  // where a lane cites something with no link.
  url: string | null;
}

interface QuestionSlide extends Slide {
  kind: "question";
  questionId: string;
  people: Person[];
}

type HeroSlide = PracticeSlide | QuestionSlide;

interface Manifest {
  practices: Array<{ do: string; image: string; chip: string | null; expert: string; url: string | null }>;
  questions: Array<{ id: string; question: string; image: string; people: Person[] }>;
}

const MANIFEST = heroart as Manifest;

const PRACTICES: PracticeSlide[] = MANIFEST.practices.map((p) => ({
  kind: "practice",
  // The manifest holds one image per practice, so it is already the stable key.
  id: p.image,
  text: p.do,
  image: p.image,
  chip: p.chip,
  expert: p.expert,
  url: p.url,
}));

const QUESTIONS: QuestionSlide[] = (MANIFEST.questions ?? []).map((q) => ({
  kind: "question",
  id: `q:${q.id}`,
  questionId: q.id,
  text: q.question,
  image: q.image,
  people: q.people,
  // A second longer than the same length of practice (2 Aug 2026, Rob):
  // "there's more names to read". The engine sets the hold from the line, and
  // under this line there are two or three names where a practice has one.
  holdBonusMs: 1000,
}));

// One question every third slide. Both pools are shuffled first, so the mix is
// the same shape on every visit while the material is not: shuffling the two
// together instead would have left a visit that opens on four questions in a
// row, or one that reaches its ninth practice before the first disagreement.
// Whatever is left over when the shorter pool runs out goes on the end, which
// no session gets near.
const EVERY = 3;

/* Which of the two the visit opens on is a coin toss (2 Aug 2026, Rob), and
   the cadence is the same either way: practice, practice, question, or
   question, practice, practice. It always opened on a practice, on the
   argument that the tagline sits directly above the picture and a practice
   under it is the proof, which is true and still leaves half the visitors
   never seeing what the second half of the product is unless they wait out
   two slides. The phase is all that moves; nothing else here knows. */
function interleave(practices: PracticeSlide[], questions: QuestionSlide[]): HeroSlide[] {
  const out: HeroSlide[] = [];
  const phase = Math.random() < 0.5 ? 0 : 2;
  let p = 0;
  let q = 0;
  while (p < practices.length || q < questions.length) {
    const wantQuestion = (out.length + phase + 1) % EVERY === 0;
    const take = wantQuestion ? questions[q] : practices[p];
    if (take) {
      out.push(take);
      if (wantQuestion) q++;
      else p++;
      continue;
    }
    // The pool this position wanted is empty: the rest of the other one runs
    // out unbroken.
    while (p < practices.length) out.push(practices[p++] as PracticeSlide);
    while (q < questions.length) out.push(questions[q++] as QuestionSlide);
  }
  return out;
}

/* Each name is its own link, to where that person actually said it (2 Aug
   2026, Rob). One link on all of them, pointing at our page, was the front
   door taking the reader to itself: the practice slide beside it has sent
   people to the expert's own source since the day it shipped, and a
   disagreement is the case where that matters most, because the whole claim
   is that these two published something incompatible. Now the byline is the
   evidence: two names, two destinations, on two different sites.

   "A and B" for a pair, three names for three, and past that the extra names
   give way to a count rather than running the byline off the frame. Lane
   order throughout: any sorting here would be the front door deciding who is
   named first in an argument. A person whose lane cites something unlinkable
   is plain text, never a dead link.

   These are the only links on a question slide. The public page is reached by
   being shared out of the question modal and in no other way from inside the
   product (2 Aug 2026, Rob). */
function Byline({ people }: { people: Person[] }) {
  const shown = people.length <= 3 ? people : people.slice(0, 2);
  const rest = people.length - shown.length;
  return (
    <>
      {shown.map((p, i) => (
        <Fragment key={p.name}>
          {i > 0 && (i === shown.length - 1 && rest === 0 ? " and " : ", ")}
          {p.url ? (
            <a href={p.url} target="_blank" rel="noreferrer noopener" className="hslide-src">
              {p.name}
            </a>
          ) : (
            p.name
          )}
        </Fragment>
      ))}
      {/* Plain text, no link (2 Aug 2026, Rob). The obvious destination for it
          was the public question page, and that page is not a door into the
          product from inside the product: see the note on the label below. */}
      {rest > 0 && ` and ${rest} more`}
    </>
  );
}

export function HeroMosaic() {
  // Shuffled per visit, and the manifest holds at most three practices per
  // expert, so a run of slides is a run of different people. The order is
  // fixed at mount: the engine plays the deck as given.
  const deck = useRef<HeroSlide[]>(interleave(shuffled(PRACTICES), shuffled(QUESTIONS)));
  const stage = useRef<HTMLDivElement>(null);
  // The clock waits for the reader (2 Aug 2026, Rob). The front door runs to
  // a second panel of 154 faces, and until today the hero kept turning the
  // whole time someone was down there reading them, so coming back up landed
  // on whatever slide the timer had reached rather than the one they left.
  const seen = useMajorityVisible(stage);
  const { cur, prev, corner } = useSlideshow(deck.current, {
    paused: !seen,
    sizes: HERO_SIZES_SOLO,
  });
  // True until the deck first moves on, which is what makes the priority hint
  // below apply to the picture the reader actually lands on.
  const first = useRef(true);
  if (prev) first.current = false;
  if (!cur) return null;
  return (
    <div className="hslides" ref={stage}>
      {prev && (
        <figure className="hslide out" key={`${prev.id}-out`} aria-hidden="true">
          <img className="hslide-img" alt="" {...artImg(prev.image)} sizes={HERO_SIZES_SOLO} />
        </figure>
      )}
      <figure className="hslide" key={cur.id} data-pos={corner}>
        {/* The front door's hero is its LCP element, so the first slide asks
            for priority. Only the first: once the reader is watching, every
            later slide has already been preloaded by the engine
            (slideshow.ts), and a permanent fetchpriority="high" would just
            mean nothing on the page is prioritised. */}
        <img
          className="hslide-img"
          alt=""
          {...artImg(cur.image)}
          sizes={HERO_SIZES_SOLO}
          fetchPriority={first.current ? "high" : undefined}
        />
        <figcaption>
          <span className="hslide-cap">
            <span className="hslide-do">{sentence(cur.text)}</span>
            {/* Plain dotted text, not pills: the shape is for things you press
                (30 Jul 2026), and the evidence label keeps no tint here because
                the four colours are not legible over a photograph.
                The name is the way out to the source when we have one on file
                (30 Jul 2026): the front door should demonstrate the thing the
                product is for, which is sending people to the expert rather
                than keeping the traffic. New tab, so the page they were
                reading is still here when they come back. */}
            {cur.kind === "practice" ? (
              <span className="hslide-from">
                From{" "}
                {cur.url ? (
                  <a href={cur.url} target="_blank" rel="noreferrer noopener" className="hslide-src">
                    {cur.expert}
                  </a>
                ) : (
                  cur.expert
                )}
                {cur.chip ? ` · ${cur.chip}` : ""}
              </span>
            ) : (
              /* The people, and the word for what this is. No position text
                 and no evidence label: quoting one side under a picture is
                 taking a side with the layout, and the question is graded
                 nowhere because a disagreement is not a claim.
                 The label is a label, never a link (2 Aug 2026, Rob): "those
                 landing pages are strictly for that, for marketing. We do not
                 want to give all our content away." /q/<id> exists to be
                 found from outside and to be shared outward, which is why the
                 question modal keeps its share control; pointing at it from
                 inside the product would be handing a visitor a way past the
                 signup for the thing the signup is for. The only links here
                 leave for the experts' own pages. */
              <span className="hslide-from">
                Crossfire · <Byline people={cur.people} />
              </span>
            )}
          </span>
        </figcaption>
      </figure>
    </div>
  );
}
